import React, { useState, useEffect, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { useQuery } from 'react-query';
import { recruitingAPI } from '../services/recruitingApi';
import './TagInput.css';

interface Skill {
  id: number;
  name: string;
  category?: string;
}

interface SkillSearchResponse {
  data?: Skill[];
}

interface TagInputProps {
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  placeholder = 'Type to search skills...',
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch skill suggestions when user types
  const { data: suggestionsData, isLoading: isLoadingSuggestions } = useQuery<SkillSearchResponse>(
    ['skills-search', inputValue],
    () => recruitingAPI.searchSkills(inputValue) as Promise<SkillSearchResponse>,
    {
      enabled: inputValue.trim().length >= 2,
      staleTime: 30000,
      retry: false
    }
  );

  const suggestions: Skill[] = suggestionsData?.data || [];

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter out already selected skills from suggestions
  const filteredSuggestions = suggestions.filter(
    skill => !value.some(selected => selected.toLowerCase() === skill.name.toLowerCase())
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(newValue.trim().length >= 2);
    setSelectedIndex(-1);
  };

  const handleInputFocus = () => {
    if (inputValue.trim().length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleAddSkill = (skillName: string) => {
    if (!skillName || skillName.trim() === '') return;

    const trimmedName = skillName.trim();

    // Check if skill already exists
    if (value.some(skill => skill.toLowerCase() === trimmedName.toLowerCase())) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }

    onChange([...value, trimmedName]);
    setInputValue('');
    setShowSuggestions(false);
    setSelectedIndex(-1);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    onChange(value.filter(skill => skill !== skillToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Enter' && showSuggestions && filteredSuggestions.length > 0) {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
        handleAddSkill(filteredSuggestions[selectedIndex].name);
      } else if (inputValue.trim() && filteredSuggestions.length > 0) {
        // Add first suggestion if no selection
        handleAddSkill(filteredSuggestions[0].name);
      } else if (inputValue.trim()) {
        // Add custom skill if no suggestions
        handleAddSkill(inputValue.trim());
      }
    } else if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      handleAddSkill(inputValue.trim());
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0) {
        setSelectedIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        setShowSuggestions(true);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (showSuggestions) {
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      handleRemoveSkill(value.at(-1) as string);
    }
  };

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  return (
    <div className="tag-input-container" ref={containerRef}>
      <div className="tag-input-wrapper">
        {/* Selected tags */}
        {value.map((skill) => (
          <span key={`tag-${skill}`} className="tag-input-tag">
            {skill}
            {!disabled && (
              <button
                type="button"
                className="tag-input-remove"
                onClick={() => handleRemoveSkill(skill)}
                aria-label={`Remove ${skill}`}
              >
                ×
              </button>
            )}
          </span>
        ))}

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          className="tag-input-field"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled}
        />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div className="tag-input-suggestions" ref={suggestionsRef}>
          {isLoadingSuggestions ? (
            <div className="tag-input-suggestion-item">Loading...</div>
          ) : filteredSuggestions.length > 0 ? (
            filteredSuggestions.map((skill, index) => (
              <div
                key={skill.id}
                className={`tag-input-suggestion-item ${
                  index === selectedIndex ? 'selected' : ''
                }`}
                onClick={() => handleAddSkill(skill.name)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddSkill(skill.name)}
                onMouseEnter={() => setSelectedIndex(index)}
                role="option"
                tabIndex={0}
                aria-selected={index === selectedIndex}
              >
                <span className="tag-input-suggestion-name">{skill.name}</span>
                <span className="tag-input-suggestion-category">{skill.category}</span>
              </div>
            ))
          ) : inputValue.trim().length >= 2 ? (
            <div
              className={`tag-input-suggestion-item ${
                selectedIndex === -1 && filteredSuggestions.length === 0 ? 'selected' : ''
              }`}
              onClick={() => handleAddSkill(inputValue.trim())}
              onMouseEnter={() => setSelectedIndex(-1)}
            >
              <span className="tag-input-suggestion-name">
                Add "{inputValue.trim()}" as custom skill
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default TagInput;
